import React from 'react'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import ProfileAvatar from '../../common/avatar/ProfileAvatar'

import { ReactComponent as CloseIcon } from '../../../assets/images/common/icons/modal-X-icon.svg'
import { ReactComponent as PersonIcon } from '../../../assets/images/common/icons/person-icon.svg'
import { ReactComponent as FamilyIcon } from '../../../assets/images/common/icons/family-icon.svg'
import { ReactComponent as CallIcon } from '../../../assets/images/common/icons/call-icon.svg'
import { ReactComponent as MailIcon } from '../../../assets/images/common/icons/mail-icon.svg'
import { ReactComponent as CopyIcon } from '../../../assets/images/common/icons/copy-icon.svg'

import { GENDER_LABEL_KEYS } from '../../../utility/userHelper'
import { formatPhoneForLinks } from '../../../utility/phoneHelper'

import classes from './DetailsDrawer.module.scss'
import colors from '../../../assets/styles/globals.scss'

const DetailsDrawer = ({ student, onClose }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const familyAdmin = student.family?.users.filter(user => user.familyAdmin)[0]

  const studentGender = t(GENDER_LABEL_KEYS[student.gender])
  const adminGenderInitial = t(GENDER_LABEL_KEYS[familyAdmin.gender]).at(0)

  const handleCopy = (e, url) => {
    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard.writeText(url)
    toast.success(t('members.detailsDrawer.copiedEmail'))
  }

  return (
    <>
      <div className={classes.drawer}>
        <header>
          <button className={classNames('d-flex align-items-center', classes.closeButton)} onClick={onClose}>
            <CloseIcon className={classNames(classes.closeIcon)} aria-hidden='true' />
          </button>
        </header>
        <main>
          <div className='d-flex mb-5'>
            <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
            <div className='d-flex flex-column flex-grow-1 pr-3'>
              <h6 className='mb-1 font-weight-bold'>{student.firstName} {student.lastName}</h6>
              <span>
                {t('members.ageYrsGender', { age: student.age ?? '-', gender: studentGender })}
              </span>
            </div>
          </div>
          <div className={classes.quickActions}>
            <h6 className='font-weight-bold mb-0'>{t('members.detailsDrawer.quickActions')}</h6>
            <button
              className={classes.quickButton}
              onClick={() => navigate(`/profile/${student.id}`)}
            >
              <PersonIcon width={22} height={22} color={colors.secondaryColor} />
              {t('common.action.viewProfile')}
            </button>
            <button
              className={classes.quickButton}
              onClick={() => navigate(`/members/families/${student.family.id}`)}
            >
              <FamilyIcon width={22} height={22} color={colors.secondaryColor} />
              {t('members.detailsDrawer.viewFamilyPage')}
            </button>
          </div>
          <div className={classes.contact}>
            <h6 className='font-weight-bold mb-0'>{t('members.contactFamilyAdmin')}</h6>
            <div className={classes.profile}>
              <ProfileAvatar student={familyAdmin} />
              <div className={classes.info}>
                <div className={classes.name}>
                  <span>{familyAdmin.firstName} {familyAdmin.lastName}</span>
                  <span className={classes.familyAdminLabel}>{t('members.familyAdmin')}</span>
                </div>
                <span>
                  {t('members.ageGenderLabel', { age: familyAdmin.age ?? '-', gender: adminGenderInitial })}
                </span>
              </div>
            </div>
            {familyAdmin.phonePrefix && familyAdmin.phone && (
              <a
                className={classes.phoneButton}
                href={familyAdmin.phone ? `tel:${formatPhoneForLinks(familyAdmin.phonePrefix, familyAdmin.phone)}` : '#'}
              >
                <CallIcon className={classes.contactIcon} />
                {familyAdmin.phonePrefix} {familyAdmin.phone || ''}
              </a>
            )}
            <a
              href={familyAdmin.email ? `mailto:${familyAdmin.email}` : '#'}
              className={classes.mailButton}
            >
              <MailIcon className={classes.contactIcon} />
              <div className={classes.emailWrapper}>
                <button
                  onClick={(e) => handleCopy(e, familyAdmin.email)}
                  className={classes.copyButton}
                  aria-label={t('members.detailsDrawer.copyEmailAria')}
                >
                  <CopyIcon className={classes.copyIcon} />
                </button>
                {familyAdmin.email || '-'}
              </div>
            </a>
          </div>
        </main>
      </div>
      <div
        tabIndex={0}
        role='button'
        onKeyDown={() => null}
        className={classes.drawerOverlay}
        onClick={onClose}
      />
    </>
  )
}

DetailsDrawer.propTypes = {
  student: PropTypes.object.isRequired,
  onClose: PropTypes.func,
}

export default DetailsDrawer
